import React, { useState, useEffect } from 'react';
import planCuentasService from '../services/planCuentasService';
import PlanCuentasTree from '../components/PlanCuentasTree';
import PlanCuentaModal from '../components/PlanCuentaModal';
import Loading from '../components/Loading';
import './PlanCuentas.css';

const PlanCuentas = ({ user }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCuenta, setSelectedCuenta] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [viewMode, setViewMode] = useState('tree'); // 'tree' o 'list'
  const [tipoCuentaFilter, setTipoCuentaFilter] = useState('TODAS');

  // Obtener cuenta activa del usuario
  const cuentaActiva = user?.cuenta_activa?.id;

  useEffect(() => {
    if (cuentaActiva) {
      loadTree();
    }
  }, [cuentaActiva]);

  const loadTree = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await planCuentasService.getTree(cuentaActiva, true);
      setTree(response.data);
    } catch (err) {
      console.error('Error al cargar plan de cuentas:', err);
      setError('Error al cargar el plan de cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoot = () => {
    setSelectedCuenta(null);
    setSelectedParent(null);
    setIsModalOpen(true);
  };

  const handleCreateChild = (parent) => {
    setSelectedCuenta(null);
    setSelectedParent(parent);
    setIsModalOpen(true);
  };

  const handleEdit = (cuenta) => {
    setSelectedCuenta(cuenta);
    setSelectedParent(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (cuenta) => {
    if (!window.confirm(`¿Estás seguro de eliminar la cuenta "${cuenta.nombre}"?`)) {
      return;
    }

    try {
      await planCuentasService.delete(cuenta.id);
      await loadTree();
    } catch (err) {
      console.error('Error al eliminar cuenta:', err);
      alert('Error al eliminar la cuenta. Puede que tenga cuentas hijas asociadas.');
    }
  };

  const handleModalClose = async (saved) => {
    setIsModalOpen(false);
    setSelectedCuenta(null);
    setSelectedParent(null);

    if (saved) {
      await loadTree();
    }
  };

  const getTiposCuenta = () => {
    return [
      { value: 'TODAS', label: 'Todas' },
      { value: 'ACTIVO', label: 'Activo' },
      { value: 'PASIVO', label: 'Pasivo' },
      { value: 'PATRIMONIO', label: 'Patrimonio' },
      { value: 'INGRESO', label: 'Ingreso' },
      { value: 'GASTO', label: 'Gasto' },
    ];
  };

  const filterTreeByTipo = (nodes) => {
    if (tipoCuentaFilter === 'TODAS') return nodes;

    return nodes.filter(node => {
      const matchesTipo = node.tipo_cuenta === tipoCuentaFilter;
      const hasMatchingChildren = node.children && filterTreeByTipo(node.children).length > 0;

      if (matchesTipo) {
        return true;
      }
      if (hasMatchingChildren) {
        return true;
      }
      return false;
    }).map(node => ({
      ...node,
      children: node.children ? filterTreeByTipo(node.children) : []
    }));
  };

  const filteredTree = filterTreeByTipo(tree);

  if (!cuentaActiva) {
    return (
      <div className="plan-cuentas-container">
        <div className="alert alert-warning">
          No tienes una cuenta activa asignada. Contacta al administrador.
        </div>
      </div>
    );
  }

  return (
    <div className="plan-cuentas-container">
      <div className="plan-cuentas-header">
        <div className="header-left">
          <h1>Plan de Cuentas</h1>
          <span className="cuenta-badge">{user?.cuenta_activa?.nombre}</span>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={handleCreateRoot}
          >
            Nueva Cuenta Raíz
          </button>
        </div>
      </div>

      <div className="plan-cuentas-filters">
        <div className="filter-group">
          <label>Tipo de Cuenta:</label>
          <select
            value={tipoCuentaFilter}
            onChange={(e) => setTipoCuentaFilter(e.target.value)}
            className="filter-select"
          >
            {getTiposCuenta().map(tipo => (
              <option key={tipo.value} value={tipo.value}>
                {tipo.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Vista:</label>
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'tree' ? 'active' : ''}`}
              onClick={() => setViewMode('tree')}
            >
              Árbol
            </button>
            <button
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              Lista
            </button>
          </div>
        </div>
      </div>

      <div className="plan-cuentas-content">
        {loading ? (
          <Loading />
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={loadTree} className="btn-secondary">
              Reintentar
            </button>
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="empty-state">
            <p>No hay cuentas en el plan.</p>
            <p className="empty-subtitle">Comienza creando una cuenta raíz.</p>
            <button onClick={handleCreateRoot} className="btn-primary">
              Crear Primera Cuenta
            </button>
          </div>
        ) : (
          <PlanCuentasTree
            tree={filteredTree}
            onCreateChild={handleCreateChild}
            onEdit={handleEdit}
            onDelete={handleDelete}
            viewMode={viewMode}
          />
        )}
      </div>

      {isModalOpen && (
        <PlanCuentaModal
          cuenta={selectedCuenta}
          parent={selectedParent}
          cuentaActiva={cuentaActiva}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default PlanCuentas;
